import type { LoadEstimateSupplierResult } from '@/modules/supply/utils/loadEstimate';
import type { LoadEstimateDraft, LoadEstimateLinePayload } from '@/services/loadEstimateService';

export const LOAD_ESTIMATE_KEY = 'supply.loadEstimate';

/** Fila de la tabla de proveedores: cifras de la estimación (o del pedido, mientras no se estima). */
export interface LoadEstimateRow {
  supplier_id: number;
  supplier_name: string;
  kind: 'validated' | 'suggested';
  source: LoadEstimateSupplierResult | undefined;
  products: number;
  amount: number;
  volume: number | null;
  volumePct: number | null;
  weight: number | null;
  weightPct: number | null;
  boxes: number;
  pallets: number;
  usableVolume: number;
  usableWeight: number;
}

export function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message ? message : fallback;
}

export const num = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** El motor devuelve textos técnicos: se traducen los casos conocidos a algo que el comprador pueda resolver. */
export function friendlyReason(reason: string, t: (key: string) => string): string {
  const text = reason.toLowerCase();
  if (text.includes('regla log')) return t(`${LOAD_ESTIMATE_KEY}.reasonNoRule`);
  if (text.includes('lines no puede estar vac')) return t(`${LOAD_ESTIMATE_KEY}.reasonNoLines`);
  return reason;
}

export const containerCodeOf = (source: LoadEstimateSupplierResult) => {
  const container = source.result.container || {};
  return String(container.container_code || container.code || '');
};

/** Producto que el pedido (sugerido o validado) de más de un proveedor marcado comparte. */
export interface ProductConflict {
  productId: number;
  productName: string;
  sku: string;
  supplierIds: number[];
}

/**
 * El borrador admite una sola línea por producto (sin importar el proveedor): si dos proveedores marcados
 * tienen el mismo producto en su pedido, hay que elegir uno antes de guardar o el backend lo rechaza entero.
 * Se mira también a los proveedores no estimables: sus productos siguen entrando en el borrador.
 */
export function findProductConflicts(sources: LoadEstimateSupplierResult[], marked: number[]): ProductConflict[] {
  const byProduct = new Map<number, ProductConflict>();
  sources
    .filter((source) => marked.includes(source.supplierId))
    .forEach((source) => {
      (source.result.before?.lines_detail ?? []).forEach((line) => {
        const productId = num(line.product_id);
        if (productId <= 0) return;
        const entry = byProduct.get(productId)
          ?? { productId, productName: line.product_name ? String(line.product_name) : `#${productId}`, sku: line.sku ? String(line.sku) : '', supplierIds: [] };
        if (!entry.supplierIds.includes(source.supplierId)) entry.supplierIds.push(source.supplierId);
        byProduct.set(productId, entry);
      });
    });
  return [...byProduct.values()].filter((entry) => entry.supplierIds.length > 1);
}

function draftLines(sources: LoadEstimateSupplierResult[], previous: LoadEstimateDraft | null): LoadEstimateLinePayload[] {
  const suggestedBefore = new Map((previous?.lines ?? []).map((line) => [line.product_id, num(line.suggested_quantity)]));
  return sources.flatMap(({ supplierId, result }) =>
    (result.before?.lines_detail ?? []).flatMap((line) => {
      const productId = num(line.product_id);
      const quantity = num(line.suggested_units ?? line.suggested_quantity);
      if (productId <= 0) return [];
      const suggested = suggestedBefore.get(productId) ?? quantity;
      return [{ product_id: productId, supplier_id: supplierId, quantity, suggested_quantity: suggested, is_user_edited: quantity !== suggested }];
    }),
  );
}

/** Lo que se guarda del borrador: las líneas y el contenedor de cada proveedor marcado. */
export function draftPayload(sources: LoadEstimateSupplierResult[], previous: LoadEstimateDraft | null, applyMoqMp: boolean) {
  return {
    apply_moq_mp: applyMoqMp,
    container_by_supplier: Object.fromEntries(
      sources.filter((source) => !source.unavailableReason).map((source) => [String(source.supplierId), containerCodeOf(source)]),
    ),
    lines: draftLines(sources, previous),
  };
}

export interface SupplierTotals {
  products: number;
  boxes: number;
  pallets: number;
  amount: number | null;
  volume: number | null;
  weight: number | null;
  volumePct: number | null;
  weightPct: number | null;
  /** Capacidad del contenedor del proveedor (m³ y kg). */
  usableVolume: number;
  usableWeight: number;
}

/** Cifras de un proveedor a partir de su estimación (con las cantidades vigentes de cada línea). */
export function totalsOf(source: LoadEstimateSupplierResult, fallbackAmount: number): SupplierTotals {
  const lines = source.result.before?.lines_detail ?? [];
  let boxes = 0;
  let pallets = 0;
  let amount = 0;
  let priced = false;
  lines.forEach((line) => {
    const quantity = num(line.suggested_units ?? line.suggested_quantity);
    const perBox = num(line.units_per_box);
    const perPallet = num(line.units_per_pallet);
    if (perBox > 0) boxes += quantity / perBox;
    if (perPallet > 0) pallets += quantity / perPallet;
    if (num(line.unit_price) > 0) {
      amount += quantity * num(line.unit_price);
      priced = true;
    }
  });
  const container = source.result.container || {};
  const usableVolume = num(container.usable_volume_m3);
  const usableWeight = num(container.usable_payload_kg);
  const available = !source.unavailableReason;
  const volume = available && source.result.before?.volume_m3 != null ? num(source.result.before.volume_m3) : null;
  const weight = available && source.result.before?.weight_kg != null ? num(source.result.before.weight_kg) : null;
  return {
    products: lines.length,
    boxes,
    pallets: Math.ceil(pallets - 1e-9),
    amount: priced ? amount : fallbackAmount,
    volume,
    weight,
    volumePct: volume != null && usableVolume > 0 ? (volume / usableVolume) * 100 : null,
    weightPct: weight != null && usableWeight > 0 ? (weight / usableWeight) * 100 : null,
    usableVolume: available ? usableVolume : 0,
    usableWeight: available ? usableWeight : 0,
  };
}
